/**
 * Jul-2026 (User-Wunsch) — „Dokumente testen"-Unterbereich (Layout & Branding).
 * Erzeugt ein Sammel-Muster-PDF ODER — über das Dropdown — gezielt EIN einzelnes
 * Musterdokument (Backend: GET /dealer/branding/samples.pdf?index=…).
 *
 * Anzeige INLINE in einem Modal. Statt Blob-URL (in der Sandbox-Preview
 * geblockt: net::ERR_ABORTED / ERR_BLOCKED_BY_CLIENT) zeigt das iframe DIREKT
 * auf die same-origin Backend-URL — Auth über httpOnly-Cookie, zusätzlich
 * ?token= als Fallback. So rendert der PDF-Viewer des Browsers normal.
 */
import { useEffect, useState } from "react";
import { FileSignature, Loader2, ChevronDown, X, Download } from "lucide-react";
import api, { API } from "../../lib/api";

export default function DocumentSamplesCard() {
    const [open, setOpen] = useState(false);
    const [docs, setDocs] = useState([]);
    const [selected, setSelected] = useState("");
    const [loadingAll, setLoadingAll] = useState(false);
    const [loadingOne, setLoadingOne] = useState(false);
    const [preview, setPreview] = useState(null); // { url, title, filename }

    useEffect(() => {
        if (!open || docs.length) return;
        api.get("/dealer/branding/samples")
            .then((r) => setDocs(r.data?.documents || []))
            .catch(() => { /* Liste optional — Sammel-PDF bleibt nutzbar */ });
    }, [open, docs.length]);

    const buildUrl = (index, ext = "pdf", page = null) => {
        const token = sessionStorage.getItem("tw_token") || "";
        const params = new URLSearchParams();
        if (index != null) params.set("index", String(index));
        if (page != null) params.set("page", String(page));
        if (token) params.set("token", token);
        const qs = params.toString();
        return `${API}/dealer/branding/samples.${ext}${qs ? `?${qs}` : ""}`;
    };

    // Jul-2026 (User-Bug „Vorschau nicht möglich") — PDFs im iframe werden von
    // vielen Browsern (v. a. mobil) nicht gerendert. Die Vorschau zeigt daher
    // server-seitig gerenderte PNG-Seiten; das PDF gibt's weiter per Download.
    const openPreview = async (index) => {
        const isOne = index != null;
        (isOne ? setLoadingOne : setLoadingAll)(true);
        const title = isOne ? (docs.find((d) => d.index === index)?.title || "Dokument") : "Alle Dokumente";
        const filename = isOne
            ? `muster-${(title || "dokument").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`
            : "dokumente-muster.pdf";
        try {
            const path = index != null
                ? `/dealer/branding/samples.png?index=${index}&page=0`
                : "/dealer/branding/samples.png?page=0";
            const r = await api.get(path, { responseType: "blob" });
            const pages = parseInt(r.headers["x-total-pages"] || "1", 10) || 1;
            setPreview({ index, title, filename, url: buildUrl(index), pages });
        } catch {
            // Fallback: Modal trotzdem öffnen — Download/Neuer-Tab funktionieren.
            setPreview({ index, title, filename, url: buildUrl(index), pages: 1 });
        } finally {
            (isOne ? setLoadingOne : setLoadingAll)(false);
        }
    };

    const closePreview = () => setPreview(null);

    return (
        <div className="border-b border-gray-200" data-testid="document-samples-card">
            <div className="flex items-center bg-gray-50 hover:bg-gray-100 border-b border-gray-200">
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    className="flex-1 flex items-center gap-3 px-5 py-3 text-left min-w-0"
                    aria-expanded={open}
                    data-testid="document-samples-toggle"
                >
                    <FileSignature className="w-4 h-4 text-[#FFCC00] shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm text-[#111827]">Dokumente testen (Muster-PDF)</div>
                        <div className="text-[11px] text-gray-500">Muster mit Ihren echten Stammdaten — alle Dokumente oder gezielt ein einzelnes.</div>
                    </div>
                    <span className={`text-gray-400 text-lg transition-transform shrink-0 ${open ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
                </button>
            </div>

            {open && (
                <div className="px-5 py-4 space-y-4" data-testid="document-samples-body">
                    {/* Alle Dokumente */}
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                            <div className="font-bold text-sm text-[#111827]">Alle Dokumente</div>
                            <div className="text-xs text-gray-500 mt-0.5">EIN Muster-PDF mit je einer Seite pro Dokumenttyp — zur Sichtprüfung von Kopf- &amp; Fußzeile.</div>
                        </div>
                        <button
                            type="button"
                            disabled={loadingAll}
                            onClick={() => openPreview(null)}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm bg-[#111827] hover:bg-black text-[#FFCC00] text-sm font-bold disabled:opacity-60 shrink-0"
                            data-testid="test-all-documents-btn"
                        >
                            {loadingAll ? <><Loader2 className="w-4 h-4 animate-spin" /> Wird erstellt …</> : "Muster-PDF anzeigen"}
                        </button>
                    </div>

                    <div className="border-t border-gray-100" />

                    {/* Einzelnes Dokument */}
                    <div>
                        <div className="font-bold text-sm text-[#111827]">Einzelnes Dokument</div>
                        <div className="text-xs text-gray-500 mt-0.5 mb-2">Nur einen bestimmten Dokumenttyp als Muster anzeigen.</div>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <div className="relative flex-1">
                                <select
                                    value={selected}
                                    onChange={(e) => setSelected(e.target.value)}
                                    className="w-full h-9 pl-3 pr-9 rounded-sm border border-gray-300 bg-white text-sm text-[#111827] appearance-none focus:outline-none focus:ring-2 focus:ring-[#FFCC00]/50 disabled:opacity-60"
                                    disabled={!docs.length}
                                    data-testid="document-samples-select"
                                >
                                    <option value="">{docs.length ? "Dokumenttyp wählen …" : "Wird geladen …"}</option>
                                    {docs.map((d) => (
                                        <option key={d.index} value={d.index}>{d.title}</option>
                                    ))}
                                </select>
                                <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                            <button
                                type="button"
                                disabled={selected === "" || loadingOne}
                                onClick={() => openPreview(Number(selected))}
                                className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-sm border border-[#111827] text-[#111827] hover:bg-[#111827] hover:text-[#FFCC00] text-sm font-bold disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#111827] shrink-0"
                                data-testid="test-single-document-btn"
                            >
                                {loadingOne ? <><Loader2 className="w-4 h-4 animate-spin" /> Wird erstellt …</> : "Dokument anzeigen"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Inline-Vorschau (Popup-Blocker-sicher) */}
            {preview && (
                <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-3 sm:p-6" data-testid="document-samples-preview">
                    <div className="bg-white rounded-sm w-full max-w-4xl h-[88vh] flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 bg-gray-50">
                            <div className="font-bold text-sm text-[#111827] truncate">Muster-Vorschau: {preview.title}</div>
                            <div className="flex items-center gap-2 shrink-0">
                                <a
                                    href={preview.url}
                                    download={preview.filename}
                                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-sm bg-[#111827] text-[#FFCC00] text-xs font-bold hover:bg-black"
                                    data-testid="document-samples-download"
                                >
                                    <Download className="w-3.5 h-3.5" /> Download
                                </a>
                                <button
                                    type="button"
                                    onClick={closePreview}
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-sm border border-gray-300 text-gray-600 hover:bg-gray-100"
                                    data-testid="document-samples-preview-close"
                                    aria-label="Schließen"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 w-full bg-gray-200 overflow-y-auto p-2 sm:p-4" data-testid="document-samples-pages">
                            {Array.from({ length: preview.pages }, (_, p) => (
                                <img
                                    key={p}
                                    src={buildUrl(preview.index, "png", p)}
                                    alt={`Seite ${p + 1}`}
                                    className="w-full max-w-3xl mx-auto mb-3 shadow border border-gray-300 bg-white"
                                    loading={p > 1 ? "lazy" : "eager"}
                                    data-testid={`document-samples-page-${p}`}
                                />
                            ))}
                        </div>
                        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-center">
                            <a href={preview.url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-[#111827] hover:underline inline-flex items-center gap-1.5" data-testid="document-samples-open-newtab">
                                <Download className="w-3.5 h-3.5" /> PDF in neuem Tab öffnen
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
